import { parseJSON } from 'date-fns';

export interface DateRecord {
    created_at?: string;
    updated_at?: string;
}

function parseRecordDate(value: string | undefined): Date | undefined {
    if (!value) {
        return undefined;
    }
    const date = parseJSON(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
}

export function getTimeGroup(date: Date | undefined): string {
    if (!date || Number.isNaN(date.getTime())) {
        return 'undefined';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const last7Days = new Date(today);
    last7Days.setDate(last7Days.getDate() - 7);
    const last30Days = new Date(today);
    last30Days.setDate(last30Days.getDate() - 30);
    const startOfYear = new Date(today.getFullYear(), 0, 1);

    if (date >= today) {
        return 'Today';
    } else if (date >= yesterday) {
        return 'Yesterday';
    } else if (date >= last7Days) {
        return 'Previous 7 Days';
    } else if (date >= last30Days) {
        return 'Previous 30 Days';
    } else if (date >= startOfYear) {
        return new Date(date).toLocaleString('en-US', { month: 'long' });
    } else {
        return date.getFullYear().toString();
    }
}

export function groupSessions<T extends DateRecord>(sessions: T[]) {
    const { groupedRecords, sortedGroups } = groupByDate(sessions);
    return { groupedSessions: groupedRecords, sortedGroups };
}

export function groupByDate<T extends DateRecord>(
    records: T[],
    dateField: keyof Pick<
        DateRecord,
        'updated_at' | 'created_at'
    > = 'updated_at'
): {
    groupedRecords: Record<string, T[]>;
    sortedGroups: string[];
} {
    const groups: Record<string, T[]> = {};
    records.forEach((record) => {
        const date = parseRecordDate(record[dateField] as string | undefined);
        const groupKey = getTimeGroup(date);
        if (!groups[groupKey]) {
            groups[groupKey] = [];
        }
        groups[groupKey].push(record);
    });

    Object.values(groups).forEach((group) => {
        group.sort((a, b) => {
            if (!a[dateField]) return 1;
            if (!b[dateField]) return -1;
            const aTime =
                parseRecordDate(a[dateField] as string)?.getTime() ?? 0;
            const bTime =
                parseRecordDate(b[dateField] as string)?.getTime() ?? 0;
            return bTime - aTime;
        });
    });

    const groupPriority: Record<string, number> = {
        Today: 1,
        Yesterday: 2,
        'Previous 7 Days': 3,
        'Previous 30 Days': 4,
        undefined: Number.MAX_SAFE_INTEGER
    };

    const sortedGroups = Object.keys(groups).sort((a, b) => {
        if (groupPriority[a] && groupPriority[b]) {
            return groupPriority[a] - groupPriority[b];
        }
        if (groupPriority[a]) return -1;
        if (groupPriority[b]) return 1;

        const monthNames = [...Array(12)].map((_, i) =>
            new Date(2000, i).toLocaleString('en-US', { month: 'long' })
        );
        if (monthNames.includes(a) && monthNames.includes(b)) {
            return monthNames.indexOf(b) - monthNames.indexOf(a);
        }
        return b.localeCompare(a);
    });

    return { groupedRecords: groups, sortedGroups };
}
